import { Employee, EmployeeConditionAttributes } from "types/Employee"
import { Company, RelatedCompany } from "types/Company"
import { StatusCodes } from "http-status-codes"
import ResponseError from "utils/ResponseError"
import { v4 as uuid } from "uuid"
import { CarrotPlan } from "types/CarrotPlan"
import { CarrotPlanEmployeeSettings } from "../../types/CarrotPlanEmployeeSettings"
import { RoutingInfo } from "../../types/RoutingInfo"
import { AdminUser } from "types/Admin"
import { ChangeHealthcareLogs } from "../../types/ChangeHealthcareLog"
import { Benefits } from "types/Benefits"
import { DocumentStatus } from "types/DocumentStatus"
import { CarrotPlanStepMenuItem } from "../../components/carrotplans/hooks/useCarrotPlanStepMenuItems"
import { CarrotPlanOptions } from "types/CarrotPlanOptions"
import { SprintsQualificationResponse } from "../../components/routinginfo/hooks/useGetSprintsQualification"
import { Journeys } from "../../types/Journeys"
import { MemberJourneyData } from "../../types/MemberJourney"
import { SprintsCandidacyResponse } from "../../components/routinginfo/hooks/useGetSprintsCandidacy"
import { NcqaEnrollmentResponse } from "../../components/routinginfo/hooks/useGetNcqaEnrollment"

export default class CarrotClient {
  baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  async ProcessJsonResponse<T>(response, correlationId): Promise<T> {
    if (response.status === StatusCodes.OK) {
      const responseText = await response.text()
      return responseText ? JSON.parse(responseText) : null
    }
    throw new ResponseError(response, correlationId)
  }

  createJsonHeaders(correlationId): Headers {
    const headers = new Headers()
    headers.append("X-Request-ID", correlationId)
    headers.append("Content-Type", "application/json")
    return headers
  }

  async getEmployee(employeeId: string): Promise<Employee> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/employee/${employeeId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })

    return this.ProcessJsonResponse<Employee>(response, correlationId)
  }

  async getEmployeeRoutingInfo(employeeId: number): Promise<RoutingInfo> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/employee/${employeeId}/routing-info`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })

    return this.ProcessJsonResponse<RoutingInfo>(response, correlationId)
  }

  async getPastRoutingInfo(employeeId: number): Promise<RoutingInfo[]> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/employee/${employeeId}/past-routing-info`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })

    return this.ProcessJsonResponse<RoutingInfo[]>(response, correlationId)
  }

  async getCompany(companyId: number): Promise<Company> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/company/${companyId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<Company>(response, correlationId)
  }

  async getRelatedCompanies(companyId: number): Promise<RelatedCompany[]> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/company/${companyId}/related-companies`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<RelatedCompany[]>(response, correlationId)
  }

  async getChangeHealthcareLogs(): Promise<ChangeHealthcareLogs> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/change-healthcare-logs`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<ChangeHealthcareLogs>(response, correlationId)
  }

  async getChangeHealthcareResponse(changeHealthRequestLogId): Promise<string> {
    const correlationId = uuid()
    const response = await fetch(
      `${this.baseUrl}/api/v2/change-healthcare-logs/files/response/${changeHealthRequestLogId}`,
      {
        method: "GET",
        headers: this.createJsonHeaders(correlationId),
        credentials: "include"
      }
    )
    return this.ProcessJsonResponse<string>(response, correlationId)
  }

  async getChangeHealthcareRequest(changeHealthRequestLogId): Promise<string> {
    const correlationId = uuid()
    const response = await fetch(
      `${this.baseUrl}/api/v2/change-healthcare-logs/files/request/${changeHealthRequestLogId}`,
      {
        method: "GET",
        headers: this.createJsonHeaders(correlationId),
        credentials: "include"
      }
    )
    return this.ProcessJsonResponse<string>(response, correlationId)
  }

  async getChangeHealthcareLogsForEmployeeId(employeeId, startDate, endDate): Promise<ChangeHealthcareLogs> {
    const correlationId = uuid()

    const startEndDateParams = new URLSearchParams()
    if (startDate) {
      startEndDateParams.append("startDate", startDate)
    }
    if (endDate) {
      startEndDateParams.append("endDate", endDate)
    }

    const response = await fetch(
      `${this.baseUrl}/api/v2/change-healthcare-logs/employee/${employeeId}${
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        startEndDateParams.size > 0 ? `?${startEndDateParams}` : ""
      }`,
      {
        method: "GET",
        headers: this.createJsonHeaders(correlationId),
        credentials: "include"
      }
    )
    return this.ProcessJsonResponse<ChangeHealthcareLogs>(response, correlationId)
  }

  async getChangeHealthcareLogsForCompanyId(companyId): Promise<ChangeHealthcareLogs> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/change-healthcare-logs/company/${companyId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<ChangeHealthcareLogs>(response, correlationId)
  }

  async getChangeHealthcareLogsForTradingPartnerId(tradingPartnerId): Promise<ChangeHealthcareLogs> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/change-healthcare-logs/trading-partner/${tradingPartnerId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<ChangeHealthcareLogs>(response, correlationId)
  }

  async createCarrotPlan({ employeeId, carrotPlanSteps, journeyData }): Promise<CarrotPlan> {
    const correlationId = uuid()
    const headers = new Headers()
    headers.append("X-Request-ID", correlationId)
    headers.append("Content-Type", "application/json")
    const body = JSON.stringify({
      employeeId: employeeId,
      steps: carrotPlanSteps,
      journeyData: journeyData
    })
    const response = await fetch(`${this.baseUrl}/api/v2/carrot-plans`, {
      credentials: "include",
      body,
      headers,
      method: "POST"
    })

    return this.ProcessJsonResponse<CarrotPlan>(response, correlationId)
  }

  async getCarrotPlan(carrotPlanId): Promise<CarrotPlan> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/carrot-plans/${carrotPlanId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<Promise<CarrotPlan>>(response, correlationId)
  }

  async getCarrotPlanStepMenuItems(carrotPlanId): Promise<CarrotPlanStepMenuItem[]> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/carrot-plans/${carrotPlanId}/step-menu-items`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<Promise<CarrotPlanStepMenuItem[]>>(response, correlationId)
  }

  async getAdminUser(): Promise<AdminUser> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/admin-users/me`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<Promise<AdminUser>>(response, correlationId)
  }

  async getCarrotPlansForEmployee(employeeId): Promise<Array<CarrotPlan>> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/carrot-plans?employeeId=${employeeId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<Promise<Array<CarrotPlan>>>(response, correlationId)
  }

  async getCarrotPlanEmployeeSettings(employeeId): Promise<CarrotPlanEmployeeSettings> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/carrot-plans/settings/employees/${employeeId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<Promise<CarrotPlanEmployeeSettings>>(response, correlationId)
  }

  async setMemberRejectedCarrotPlan({ employeeId, payload }): Promise<void> {
    const correlationId = uuid()
    const headers = new Headers()
    headers.append("X-Request-ID", correlationId)
    headers.append("Content-Type", "application/json")
    await fetch(`${this.baseUrl}/api/v2/carrot-plans/settings/employees/member-rejected-carrot-plan/${employeeId}`, {
      credentials: "include",
      body: payload,
      headers,
      method: "POST"
    })
  }

  async getCarrotPlanOptions(
    employeeId: number,
    journeyId?: number,
    journeyStageId?: number
  ): Promise<CarrotPlanOptions> {
    const correlationId = uuid()
    const url = new URL(`${this.baseUrl}/api/v2/carrot-plans/options/${employeeId}`)

    if (journeyId != null && journeyStageId != null) {
      url.searchParams.append("journeyId", journeyId.toString())
      url.searchParams.append("journeyStageId", journeyStageId.toString())
    }

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })

    return this.ProcessJsonResponse<CarrotPlanOptions>(response, correlationId)
  }

  async getEmployeeConditionAttributes(employeeId: number): Promise<EmployeeConditionAttributes> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/employee/${employeeId}/condition-attributes`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<EmployeeConditionAttributes>(response, correlationId)
  }

  async getBenefitsForEmployee(employeeId): Promise<Benefits> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/benefits?employeeId=${employeeId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<Promise<Benefits>>(response, correlationId)
  }

  async poseAsMember(employeeId): Promise<void> {
    const correlationId = uuid()
    await fetch(`${this.baseUrl}/api/v2/member-pose/`, {
      credentials: "include",
      headers: this.createJsonHeaders(correlationId),
      body: JSON.stringify({ employeeId: employeeId }),
      method: "POST"
    })
  }

  async getDocumentStatuses(employeeId: number): Promise<DocumentStatus[]> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/admin/document-status/${employeeId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })

    return this.ProcessJsonResponse<DocumentStatus[]>(response, correlationId)
  }

  async getAllJourneyAndJourneyStages(): Promise<Journeys> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/admin/journey/all-options`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })

    return this.ProcessJsonResponse<Journeys>(response, correlationId)
  }

  async getBenefitEnabledJourneysForEmployee(employeeId: number): Promise<Journeys> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/admin/journey/enabled-options/${employeeId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })

    return this.ProcessJsonResponse<Journeys>(response, correlationId)
  }

  async getEmployeeSprintsQualification(employeeId: number): Promise<SprintsQualificationResponse> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/admin/sprints-access/qualification/${employeeId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<Promise<SprintsQualificationResponse>>(response, correlationId)
  }

  async getEmployeeSprintsCandidacy(employeeId: number): Promise<SprintsCandidacyResponse> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/admin/sprints-access/candidacy/${employeeId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<Promise<SprintsCandidacyResponse>>(response, correlationId)
  }

  async getEmployeeNcqaEnrollmentStatus(employeeId: number): Promise<NcqaEnrollmentResponse> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/admin/ncqa-enrollment/status/${employeeId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<Promise<NcqaEnrollmentResponse>>(response, correlationId)
  }

  async getMemberJourney(employeeId: number): Promise<MemberJourneyData> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/employee/${employeeId}/member-journey`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<MemberJourneyData>(response, correlationId)
  }
}
